import { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import FormControlLabel from "@mui/material/FormControlLabel";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { GrantSpec } from "@equipped/core/backend/client";
import { CatalogNode } from "@equipped/core/content/catalogTree";
import { grantTargets, rootCatalogTargets } from "@equipped/core/content/grantTargets";

const MAX_LISTED_TARGETS = 40;

/**
 * Multi-select picker of catalogs/packs to grant on an invite, re-skinned to MUI — mirrors
 * `apps/admin-web/src/components/GrantPicker.tsx` (itself mirroring `apps/admin/ui/
 * InviteGrantPicker.tsx`): the default list is TOP-LEVEL root catalogs only
 * (`rootCatalogTargets`), with a "Show packs & sub-catalogs" switch revealing the finer
 * `grantTargets()` list; tapping a row (or a selected chip) toggles it in/out of `selected`. The
 * host owns the `GrantSpec[]` state — the SAME `@equipped/core` helpers this reuses unchanged.
 */
export function GrantPicker({
  tree,
  baseUrl,
  selected,
  onToggle,
  error,
}: {
  tree: CatalogNode | null;
  baseUrl: string;
  selected: GrantSpec[];
  onToggle: (kind: "pack" | "catalog", ref: string) => void;
  /** The owning `useCatalogTree()` load, when it failed (docs/340-ADMIN-MANTIS-AND-
   *  API-IDEAS.md §3) — shown instead of the generic "may still be loading" copy so a
   *  genuine fetch failure doesn't read as an indefinite spinner-less wait. */
  error?: string | null;
}) {
  const [q, setQ] = useState("");
  const [showPacks, setShowPacks] = useState(false);
  const targets = useMemo(() => grantTargets(tree, baseUrl), [tree, baseUrl]);
  const selectedKeys = useMemo(() => new Set(selected.map((g) => `${g.kind}:${g.ref}`)), [selected]);

  if (targets.length === 0) {
    return (
      <Typography color={error ? "error" : "text.secondary"} variant="body2" sx={{ mb: 0 }}>
        {error
          ? `Couldn't load the catalog tree: ${error}`
          : "No catalogs/packs available yet — the catalog tree may still be loading."}
      </Typography>
    );
  }

  const roots = rootCatalogTargets(tree, baseUrl);
  const rootKeys = new Set(roots.map((r) => `${r.kind}:${r.ref}`));
  const rest = targets.filter((tg) => !rootKeys.has(`${tg.kind}:${tg.ref}`));
  const listed = showPacks ? [...roots, ...rest] : roots;

  const ql = q.trim().toLowerCase();
  const matching = listed.filter((tg) => !ql || `${tg.title} ${tg.ref} ${tg.path}`.toLowerCase().includes(ql));
  const filtered = matching.slice(0, MAX_LISTED_TARGETS);
  // docs/340-ADMIN-MANTIS-AND-API-IDEAS.md §3 — the list used to be silently capped at
  // MAX_LISTED_TARGETS with no indication anything was cut off, so a real provider's
  // longer catalog could hide a target with no way to reach it besides guessing a
  // narrower search. Show the truncation instead of hiding it.
  const truncated = matching.length > filtered.length;

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
        Grants (applied automatically when redeemed)
      </Typography>
      {selected.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No grants selected — this code won't unlock anything by itself.
        </Typography>
      ) : (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 1 }}>
          {selected.map((g) => (
            <Chip
              key={`${g.kind}:${g.ref}`}
              label={`${g.kind}: ${g.ref} ✕`}
              size="small"
              onClick={() => onToggle(g.kind, g.ref)}
              sx={{ cursor: "pointer" }}
            />
          ))}
        </Box>
      )}
      <TextField
        size="small"
        placeholder="Search catalogs/packs…"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        fullWidth
        sx={{ mb: 1 }}
      />
      <FormControlLabel
        control={<Switch size="small" checked={showPacks} onChange={(e) => setShowPacks(e.target.checked)} />}
        label="Show packs & sub-catalogs"
        sx={{ mb: 1 }}
      />
      <List dense sx={{ maxHeight: 220, overflowY: "auto", border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
        {filtered.map((tg) => {
          const key = `${tg.kind}:${tg.ref}`;
          const isSelected = selectedKeys.has(key);
          return (
            <ListItemButton key={key} selected={isSelected} onClick={() => onToggle(tg.kind, tg.ref)}>
              <ListItemText
                primary={`${isSelected ? "✓ " : ""}${tg.title}`}
                secondary={`${tg.kind} · ${tg.path}`}
                slotProps={{ primary: { sx: { fontWeight: 600 } }, secondary: { sx: { color: "text.secondary" } } }}
              />
            </ListItemButton>
          );
        })}
      </List>
      {truncated ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.5 }}>
          Showing {filtered.length} of {matching.length} — refine your search to see more.
        </Typography>
      ) : null}
    </Box>
  );
}
